import { MuiAlert } from '@fox/atoms/src/mui-alert/index';
import { tryParseJSONArray, tryParseJSONObject } from '@fox/atoms/utils/string';
import { FlexColumnSpaced } from '@fox/layout/src/flex';
import { ReactElement } from 'react';
import { FormRenderMode, QuestionStyleTypes } from '../../../../form-runtime-context/contracts';
import { useFormRuntimeContext, useQuestionStyle } from '../../../../form-runtime-context/form-runtime-context';
import { RequiredAsterisk } from '../../../../required-asterisk/required-asterisk';
import { DataTableCustomFieldHandling, QuestionBlockMetadata } from '../../contracts';
import { QuestionLabelStyle } from '../../question-label';
import { CheckboxCustomField } from './custom-fields/checkbox-custom-field';
import { DateCustomField } from './custom-fields/date-custom-field';
import { NumberCustomField } from './custom-fields/number-custom-field';
import { SelectCustomField } from './custom-fields/select-custom-field';
import { TextCustomField } from './custom-fields/text-custom-field';
import { TimeCustomField } from './custom-fields/time-custom-field';
import {
	DataTableAnswers,
	DataTableCustomFieldComponentArgs,
	DataTableRowAnswer,
	FIELD_CHECKBOX,
	FIELD_DATETIME,
	FIELD_NUMBER,
	FIELD_SELECT,
	FIELD_STRING,
	FIELD_TIME,
	IRenderDataTableQuestionProps
} from './helpers';

export const RenderCustomFieldDataTableQuestion = ({ id, fieldProps }: IRenderDataTableQuestionProps) => {
	const { getAnswer, answerQuestion } = useFormRuntimeContext();
	const fieldMeta = tryParseJSONObject<QuestionBlockMetadata>(fieldProps.metadataJson, {});

	const questionName = fieldProps.name;
	const dataTableCustomFields: DataTableCustomFieldHandling[] = fieldMeta?.dataTableCustomFields ?? [];

	const answer = tryParseJSONArray<DataTableRowAnswer>(getAnswer(questionName)?.answer?.answerJson);

	const style = fieldProps.style;

	const handleChange = (value: string, rowId: string, fieldId: string) => {
		const newAnswers: DataTableAnswers = answer.map(a => (a.rowId === rowId ? { ...a, [fieldId]: value } : a));
		answerQuestion({
			questionName,
			answer: {
				answerJson: JSON.stringify(newAnswers)
			}
		});
		return newAnswers;
	};

	const { fontSize, fontFamily, color, fontStyle, textDecoration, fontWeight } = useQuestionStyle(QuestionStyleTypes.label, {}, id);

	//Assume single mode for now. Multi mode will be more compilcated.
	const isSingleMode = fieldMeta?.dataTableHandling === 'single';
	if (!isSingleMode)
		return <MuiAlert severity="error">This question has not been configured properly. Multi insert mode is not supported.</MuiAlert>;

	return (
		<FlexColumnSpaced style={{ paddingLeft: '1rem' }}>
			{dataTableCustomFields
				.filter(cf => cf.show)
				.map((cf, _) => {
					return (
						<>
							<QuestionLabelStyle
								style={{
									...style,
									fontSize,
									fontFamily,
									color,
									fontStyle,
									textDecoration,
									fontWeight
								}}
							>
								{cf.label}
								{cf.required ? <RequiredAsterisk /> : null}
							</QuestionLabelStyle>
							<CustomFieldComponent cf={cf} answer={answer?.[0]} handleChange={handleChange} id={id} />
						</>
					);
				})}
		</FlexColumnSpaced>
	);
};

const CustomFieldComponent = ({
	cf,
	answer,
	id,
	handleChange
}: {
	cf: DataTableCustomFieldHandling;
	answer: DataTableRowAnswer | undefined;
	id: string;
	handleChange: (value: string, rowId: string, fieldId: string) => DataTableAnswers;
}): ReactElement => {
	const { formRenderMode } = useFormRuntimeContext();

	const defaultValueComponents = {
		[FIELD_STRING]: TextCustomField,
		[FIELD_NUMBER]: NumberCustomField,
		[FIELD_DATETIME]: DateCustomField,
		[FIELD_TIME]: TimeCustomField,
		[FIELD_CHECKBOX]: CheckboxCustomField,
		[FIELD_SELECT]: SelectCustomField
	};

	const Component = defaultValueComponents[cf.type];
	if (!Component) {
		if (formRenderMode === FormRenderMode.design || formRenderMode === FormRenderMode.preview) {
			return (
				<MuiAlert severity="error">
					This custom field type is not supported in forms. Please set a default value and hide the custom field.
				</MuiAlert>
			);
		}
		return <></>;
	}

	const cfProps: DataTableCustomFieldComponentArgs = {
		field: cf,
		row: answer,
		questionId: id,
		handleChange: handleChange
	};

	return <Component {...cfProps} />;
};
